import React from 'react';
import { Home, Building2, Clock, TrendingUp, Users, MapPin } from 'lucide-react';

const calcShopCard = (priceStr) => {
  const n = parseInt(priceStr?.replace(/[^0-9]/g, '') || '0');
  if (n >= 2000000) return 4000;
  if (n >= 1900000) return 3800;
  if (n >= 1800000) return 3600;
  if (n >= 1700000) return 3400;
  if (n >= 1600000) return 3200;
  if (n >= 1500000) return 3000;
  if (n >= 1400000) return 2800;
  if (n >= 1300000) return 2600;
  if (n >= 1200000) return 2400;
  if (n >= 1100000) return 2200;
  if (n >= 1000000) return 2000;
  if (n >= 900000) return 1800;
  if (n >= 800000) return 1600;
  if (n >= 700000) return 1400;
  if (n >= 600000) return 1200;
  if (n >= 500000) return 1000;
  if (n >= 400000) return 800;
  if (n >= 300000) return 600;
  if (n >= 200000) return 400;
  if (n >= 100000) return 200;
  return 0;
};

const SuburbSnapshot = ({ data, warehouseName, surroundingSuburbs }) => {
  if (!data) return null;

  const shopCardValue = calcShopCard(data.medianHouse);

  const stats = [
    { icon: Home, label: 'Median House Price', value: data.medianHouse },
    { icon: Building2, label: 'Median Unit Price', value: data.medianUnit },
    { icon: Clock, label: 'Avg Days on Market', value: data.daysOnMarket },
    { icon: TrendingUp, label: 'Rental Yield', value: data.rentalYield },
    { icon: Users, label: 'Population', value: data.population },
    { icon: MapPin, label: `Distance to Costco ${warehouseName}`, value: data.distanceToCostco },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-[#00594C] mb-2">
              {data.suburb} & Surrounding Suburbs Property Snapshot
            </h2>
            <p className="text-gray-500 text-sm">Data as of Q1 2026 — indicative only</p>
          </div>

          {/* Featured Suburb Stats */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#00594C] mb-4">{data.suburb}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {stats.map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center">
                    <div className="inline-flex items-center justify-center w-10 h-10 bg-[#99DF63]/15 rounded-full mb-3">
                      <Icon className="w-5 h-5 text-[#00594C]" />
                    </div>
                    <p className="text-2xl font-bold text-[#00594C] mb-1">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Shop Card at Median Price */}
          {shopCardValue > 0 && (
            <div className="bg-gradient-to-r from-[#00594C] to-[#003d35] rounded-2xl p-6 text-white text-center mb-10">
              <p className="text-sm text-gray-300 mb-1">Buy a median-priced home in {data.suburb} and earn</p>
              <p className="text-4xl font-bold text-[#99DF63] mb-1">
                ${shopCardValue.toLocaleString()} Costco Shop Card*
              </p>
              <p className="text-sm text-gray-300">
                Based on median house price of {data.medianHouse}
              </p>
            </div>
          )}

          {/* Surrounding Suburbs Table */}
          {surroundingSuburbs && surroundingSuburbs.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-[#00594C] mb-4">Surrounding Suburbs</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#00594C] text-white">
                      <th className="text-left p-3 rounded-tl-lg">Suburb</th>
                      <th className="text-right p-3">Median House</th>
                      <th className="text-right p-3">Median Unit</th>
                      <th className="text-right p-3 hidden md:table-cell">Days on Market</th>
                      <th className="text-right p-3 rounded-tr-lg">
                        <span className="text-[#99DF63]">Shop Card Value*</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {surroundingSuburbs.map((s, i) => {
                      const sc = calcShopCard(s.medianHouse);
                      return (
                        <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-[#99DF63]/5 transition-colors`}>
                          <td className="p-3 font-medium text-[#00594C]">{s.suburb}</td>
                          <td className="p-3 text-right text-gray-700">{s.medianHouse}</td>
                          <td className="p-3 text-right text-gray-700">{s.medianUnit}</td>
                          <td className="p-3 text-right text-gray-500 hidden md:table-cell">{s.daysOnMarket}</td>
                          <td className="p-3 text-right font-bold text-[#00594C]">
                            {sc > 0 ? `$${sc.toLocaleString()}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-6">
            * Based on fully drawn-down loan amount. Offset and redraw balances excluded. <a href="/costco-terms" className="text-[#00594C] underline">T&Cs apply</a>.
            Property data is indicative and sourced from publicly available information. Actual prices vary. This does not constitute financial advice.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SuburbSnapshot;
